import { Suspense, lazy, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { ShowcasePeriod } from "@/entities/robot";
import type { components } from "@/api/types";
import { tactile } from "@/shared/telegram";
import { Panel, Segmented, type SegmentedOption } from "@/shared/ui";

type ChartPoint = components["schemas"]["ChartPoint"];

/** Порядок чипов — от короткого к длинному (ТЗ §7). */
const PERIODS: readonly ShowcasePeriod[] = ["1m", "3m", "6m", "1y", "all"] as const;

const ShowcasePlot = lazy(() =>
  import("./ShowcasePlot").then((module) => ({ default: module.ShowcasePlot })),
);

/**
 * Тик по спаду `busy`: ряд догрузился и уже нарисован. Первый рендер молчит —
 * экран открылся с данными, а не «дозагрузил» их.
 */
function useLoadedTick(busy: boolean): void {
  const wasBusy = useRef(false);
  useEffect(() => {
    if (wasBusy.current && !busy) tactile("select");
    wasBusy.current = busy;
  }, [busy]);
}

/**
 * График доходности робота (ТЗ §7).
 *
 * Переключателя «Сравнить с BTC» здесь нет, хотя ТЗ его просит. Источника цены
 * BTC у проекта не существует: биржевых интеграций нет ни одной, а нарисовать
 * сгенерированную кривую и подписать её «BTC» — единственное утверждение на
 * экране, которое любой проверит за десять секунд, открыв график биржи. Тот же
 * выбор уже сделан в `widgets/equity-chart`: легенда макета объявляла ряд BTC, и
 * он был убран с формулировкой «рисовать пустые легенды нельзя — они обещают
 * данные, которых не будет» (DESIGN.md §6).
 *
 * Ряд, его окно и прореживание считает бэкенд (правило ТЗ №5): сюда приходит
 * готовое, а период уезжает в запрос.
 */
export function ShowcaseChart({
  name,
  color,
  points,
  period,
  onPeriodChange,
  busy = false,
}: {
  name: string;
  color: string;
  points: ChartPoint[];
  period: ShowcasePeriod;
  onPeriodChange: (period: ShowcasePeriod) => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  useLoadedTick(busy);

  const periods = useMemo<readonly SegmentedOption<ShowcasePeriod>[]>(
    () => PERIODS.map((item) => ({ value: item, label: t(`robots.analytics.chart.periods.${item}`) })),
    [t],
  );

  return (
    <Panel as="section" radius="lg" className="flex w-full min-w-0 flex-col gap-3 p-4">
      <h2 className="text-body font-semibold text-text-primary">
        {t("robots.analytics.chart.title", { name })}
      </h2>

      {/* Пять чипов на 320 px в строку не помещаются, поэтому группа скроллится
          сама, а не растягивает экран: правило §22 — горизонтального выхода
          контента за экран быть не должно. */}
      <div className="no-scrollbar -mx-1 overflow-x-auto px-1">
        <Segmented
          options={periods}
          value={period}
          onChange={onPeriodChange}
          ariaLabel={t("robots.analytics.chart.periodLabel")}
          variant="solid"
          className={busy ? "w-fit opacity-60" : "w-fit"}
        />
      </div>

      <div className="h-[190px] w-full">
        <Suspense fallback={<div className="shimmer size-full rounded-xl bg-background-elevated" />}>
          <ShowcasePlot points={points} color={color} />
        </Suspense>
      </div>

      <footer className="flex items-center gap-1.5">
        <span className="size-[7px] rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
        <span className="text-micro font-medium text-text-secondary">
          {t("robots.analytics.chart.legend")}
        </span>
      </footer>
    </Panel>
  );
}
